// Singleton Google Maps JS SDK loader — the SDK's own script tag must only ever be injected once
// per page, but multiple QuickQuoteMap instances can mount/unmount across a chat session (the
// Quick Quote workspace card is upserted in place, same as every other single-surface card). Each
// loadX() below memoizes its library promise so a remount reuses the same in-flight/resolved load
// instead of re-injecting the script or re-requesting the library.
import { Loader } from '@googlemaps/js-api-loader';

const loader = new Loader({
  apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  version: 'weekly',
});

let mapsPromise: Promise<typeof google.maps> | null = null;
let drawingPromise: Promise<google.maps.DrawingLibrary> | null = null;
let geometryPromise: Promise<google.maps.GeometryLibrary> | null = null;

/** Loads the core `maps` library (google.maps.Map, google.maps.Marker, …). */
export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (!mapsPromise) mapsPromise = loader.importLibrary('maps').then(() => google.maps);
  return mapsPromise;
}

/** Loads the `drawing` library. NOTE: `google.maps.drawing.DrawingManager` itself was removed from
 *  the Maps JavaScript API (Google deprecated + then dropped it — see @types/google.maps's
 *  DrawingManager, whose class body is now empty), so QuickQuoteMap does NOT use this — it builds
 *  polygon/polyline tracing directly on top of the still-fully-supported `maps` (Polygon/Polyline)
 *  and `geometry` (spherical area/length) libraries instead. Exported for completeness / in case a
 *  future caller wants `OverlayType`, which the library still exposes. */
export function loadDrawingLibrary(): Promise<google.maps.DrawingLibrary> {
  if (!drawingPromise) drawingPromise = loader.importLibrary('drawing');
  return drawingPromise;
}

/** Loads the `geometry` library (google.maps.geometry.spherical area/length helpers). */
export function loadGeometryLibrary(): Promise<google.maps.GeometryLibrary> {
  if (!geometryPromise) geometryPromise = loader.importLibrary('geometry');
  return geometryPromise;
}
